import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Loader2, GitCompare, Link2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/config";
import { useCountry } from "@/components/contexts/CountryContext";
import { usePlayer } from "@/components/contexts/PlayerContext";
import { VideoCardCompact } from "@/components/VideoCardCompact";

/**
 * The same chart in two to four regions, side by side.
 *
 * The overlap row is the point: seeing which videos are trending everywhere versus
 * only locally is something the YouTube app cannot show, and it makes the view worth
 * sharing - hence the copy-link button and URL-held state.
 */
export const ComparePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { countries } = useCountry();
  const { play } = usePlayer();

  const selected = (searchParams.get("countries") || "IN,US").split(",").filter(Boolean);
  const categoryId = searchParams.get("category_id") || "0";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selected.length < 2) return;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/compare`, {
          params: { countries: selected.join(","), category_id: categoryId, max_results: 10 },
        });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const toggleCountry = (code) => {
    const next = selected.includes(code)
      ? selected.filter((c) => c !== code)
      : [...selected, code].slice(0, 4); // four columns is the most that stays readable
    if (next.length < 2) {
      toast.error("Pick at least two countries");
      return;
    }
    setSearchParams({ countries: next.join(","), category_id: categoryId }, { replace: true });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Comparison link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="compare-page">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <GitCompare className="w-6 h-6 text-orange-500" />
          <h1 className="font-heading text-2xl sm:text-3xl font-black">Compare Countries</h1>
          <button
            onClick={copyLink}
            className="ml-auto inline-flex items-center gap-1.5 h-11 sm:h-auto px-3 sm:py-1.5 rounded-lg border border-white/15 hover:bg-white/10 text-xs font-medium transition-colors"
          >
            <Link2 className="w-3.5 h-3.5" /> Copy link
          </button>
        </div>
        <p className="text-sm text-white/50 mb-6">What is trending in each region right now — pick up to four.</p>

        {/* No inner max-height/scroll here: at 128px the third row of countries was
            sliced in half, which reads as a rendering bug rather than as something
            scrollable, and a scroll area nested in a scrolling page is awkward to
            hit on a phone. The list is short enough to just wrap. */}
        <div className="flex flex-wrap gap-1.5 mb-8">
          {countries.map((c) => (
            <button
              key={c.code}
              onClick={() => toggleCountry(c.code)}
              className={`px-2.5 py-1 min-h-[44px] sm:min-h-0 rounded-lg text-xs font-medium transition-colors ${
                selected.includes(c.code)
                  ? "bg-orange-500 text-black"
                  : "bg-white/5 border border-white/10 hover:bg-white/10"
              }`}
            >
              {c.flag} {c.code}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : !data ? (
          <p className="text-center text-sm text-white/40 py-16">Could not load the comparison.</p>
        ) : (
          <>
            {data.shared_videos?.length > 0 && (
              <section className="mb-10">
                <h2 className="font-heading text-lg font-bold mb-1">
                  Trending in all {data.columns.length} regions
                </h2>
                <p className="text-xs text-white/40 mb-4">Global hits, not local ones.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {data.shared_videos.map((v) => (
                    <VideoCardCompact key={v.id} video={v} onPlay={play} />
                  ))}
                </div>
              </section>
            )}

            {/* Columns are a horizontal scroller rather than a fixed grid.
                `repeat(N, minmax(240px, 1fr))` needed 480px for two regions and
                960px for four, so on a phone the second column ran off the screen
                and could not be reached at all - html/body carry overflow-x:hidden,
                which hid the overflow instead of allowing a scroll to it.
                grid-flow-col with auto-cols shares the space when it fits and
                scrolls when it does not, at every width, with no breakpoints. Snap
                points make swiping between regions land cleanly. */}
            <p className="text-xs text-white/40 mb-3 lg:hidden">Swipe sideways to see the other regions.</p>
            <div className="grid grid-flow-col auto-cols-[minmax(240px,1fr)] gap-6 overflow-x-auto snap-x snap-proximity pb-4 -mx-4 px-4 sm:mx-0 sm:px-0">
              {data.columns.map((column) => (
                <div key={column.country} className="min-w-0 snap-start">
                  {/* Not sticky. A pinned heading sits over the column by
                      definition - the cards scroll underneath it - so it covered
                      the first thumbnail no matter what background it had. Making
                      it opaque only changed what the overlap looked like. It also
                      escaped the scroller's px-4 when pinned, so the bar ran to the
                      screen edge and the country name was clipped on the left.
                      A heading that scrolls away with its column cannot do either. */}
                  <h2 className="font-heading text-base font-bold mb-3 pb-2 border-b border-white/10">
                    {column.country_name}
                  </h2>
                  <div className="space-y-3">
                    {column.videos.map((v, i) => (
                      <div key={v.id} className="flex gap-2">
                        <span className="shrink-0 w-5 pt-1 text-xs font-bold text-white/30">{i + 1}</span>
                        <div className="min-w-0 flex-1">
                          <VideoCardCompact video={v} onPlay={play} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
